import { camelCase, startCase } from 'lodash';

import {
  InspectionGeneralCondition as GqlInspectionGeneralCondition,
  InspectionInsurableDamages as GqlInspectionInsurableDamages,
  InspectionOccupancyStatus as GqlInspectionOccupancyStatus,
  InspectionOccupancyType as GqlInspectionOccupancyType,
  InspectionOrderedReason as GqlInspectionOrderedReason,
  InspectionType as GqlInspectionType,
} from '@willow/graphql-iso/src/app/backend';
import {
  assertNever,
  InspectionGeneralCondition,
  InspectionInsurableDamages,
  InspectionOccupancyStatus,
  InspectionOccupancyType,
  InspectionOrderedReason,
  InspectionType,
} from '@willow/types-iso';

export function getInspectionOccupancyStatusLabel(occupancyStatus: InspectionOccupancyStatus): string;
export function getInspectionOccupancyStatusLabel(occupancyStatus: GqlInspectionOccupancyStatus): string;
export function getInspectionOccupancyStatusLabel(
  occupancyStatus: InspectionOccupancyStatus | GqlInspectionOccupancyStatus,
): string {
  switch (occupancyStatus) {
    case 'vacant':
    case GqlInspectionOccupancyStatus.Vacant:
    case 'occupied':
    case GqlInspectionOccupancyStatus.Occupied:
    case 'abandoned':
    case GqlInspectionOccupancyStatus.Abandoned:
      return startCase(camelCase(occupancyStatus));
    case 'disasterUnknown':
    case GqlInspectionOccupancyStatus.DisasterUnknown:
      return 'Unable to Determine (Disaster)';
    case 'secondaryResidence':
    case GqlInspectionOccupancyStatus.SecondaryResidence:
      return 'Secondary Residence';
    case 'vacantLot':
    case GqlInspectionOccupancyStatus.VacantLot:
      return 'Vacant Lot';
    default:
      assertNever(occupancyStatus);
  }
}

export function getInspectionOccupancyTypeLabel(occupancyType: InspectionOccupancyType): string;
export function getInspectionOccupancyTypeLabel(occupancyType: GqlInspectionOccupancyType): string;
export function getInspectionOccupancyTypeLabel(
  occupancyType: InspectionOccupancyType | GqlInspectionOccupancyType,
): string {
  switch (occupancyType) {
    case 'owner':
    case GqlInspectionOccupancyType.Owner:
    case 'tenant':
    case GqlInspectionOccupancyType.Tenant:
    case 'unknown':
    case GqlInspectionOccupancyType.Unknown:
      return startCase(camelCase(occupancyType));
    default:
      assertNever(occupancyType);
  }
}

export function getInspectionGeneralConditionLabel(generalCondition: InspectionGeneralCondition): string;
export function getInspectionGeneralConditionLabel(generalCondition: GqlInspectionGeneralCondition): string;
export function getInspectionGeneralConditionLabel(
  generalCondition: InspectionGeneralCondition | GqlInspectionGeneralCondition,
): string {
  switch (generalCondition) {
    case 'c1':
    case GqlInspectionGeneralCondition.C1:
      return 'C1 - Great condition, no work needed';
    case 'c2':
    case GqlInspectionGeneralCondition.C2:
      return 'C2 - Very minor work needed';
    case 'c3':
    case GqlInspectionGeneralCondition.C3:
      return 'C3 - Repairs needed to bring up to code';
    case 'c4':
    case GqlInspectionGeneralCondition.C4:
      return 'C4 - Structural issues, out of code, needs mechanicals';
    case 'c5':
    case GqlInspectionGeneralCondition.C5:
      return 'C5 - Significant damage, work required';
    case 'c6':
    case GqlInspectionGeneralCondition.C6:
      return 'C6 - Demolition candidate';
    case 'inaccessible':
    case GqlInspectionGeneralCondition.Inaccessible:
      return 'Property is inaccessible';
    default:
      assertNever(generalCondition);
  }
}

export function getInspectionInsurableDamagesLabel(insurableDamages: InspectionInsurableDamages): string;
export function getInspectionInsurableDamagesLabel(insurableDamages: GqlInspectionInsurableDamages): string;
export function getInspectionInsurableDamagesLabel(
  insurableDamages: InspectionInsurableDamages | GqlInspectionInsurableDamages,
): string {
  switch (insurableDamages) {
    case 'fire':
    case GqlInspectionInsurableDamages.Fire:
    case 'vandalism':
    case GqlInspectionInsurableDamages.Vandalism:
    case 'wind':
    case GqlInspectionInsurableDamages.Wind:
    case 'theft':
    case GqlInspectionInsurableDamages.Theft:
    case 'water':
    case GqlInspectionInsurableDamages.Water:
    case 'notApplicable':
    case GqlInspectionInsurableDamages.NotApplicable:
      return startCase(camelCase(insurableDamages));
    default:
      assertNever(insurableDamages);
  }
}

export function getInspectionTypeLabel(inspectionType: InspectionType): string;
export function getInspectionTypeLabel(inspectionType: GqlInspectionType): string;
export function getInspectionTypeLabel(inspectionType: InspectionType | GqlInspectionType): string {
  switch (inspectionType) {
    case 'curbside':
    case GqlInspectionType.Curbside:
    case 'exteriorOnly':
    case GqlInspectionType.ExteriorOnly:
    case 'other':
    case GqlInspectionType.Other:
      return startCase(camelCase(inspectionType));
    case 'interiorAndExterior':
    case GqlInspectionType.InteriorAndExterior:
      return 'Interior/Exterior';
    default:
      assertNever(inspectionType);
  }
}

export function getInspectionOrderedReasonLabel(inspectionReason: InspectionOrderedReason): string;
export function getInspectionOrderedReasonLabel(inspectionReason: GqlInspectionOrderedReason): string;
export function getInspectionOrderedReasonLabel(
  inspectionReason: InspectionOrderedReason | GqlInspectionOrderedReason,
): string {
  switch (inspectionReason) {
    case 'collections':
    case GqlInspectionOrderedReason.Collections:
    case 'foreclosure':
    case GqlInspectionOrderedReason.Foreclosure:
    case 'lossMitigation':
    case GqlInspectionOrderedReason.LossMitigation:
    case 'other':
    case GqlInspectionOrderedReason.Other:
      return startCase(camelCase(inspectionReason));
    case 'bankruptcy':
    case GqlInspectionOrderedReason.Bankruptcy:
      return 'Bankruptcy/Litigation';
    default:
      assertNever(inspectionReason);
  }
}
